import Backbone from 'backbone'
import _ from 'lodash'
import template from './import-results-dialog.hbs'
import i18n from 'scoreboard/components/i18n'
import highlight from 'scoreboard/components/highlight'
import appDisplay from 'scoreboard/components/app-display'
import importProblemsTemplate from './import-problems-dialog.hbs'

const ImportResultsDialog = Backbone.View.extend({

	className: 'success-dialog',

	events: {
		'click .more-info': 'moreInfo',
		'click .button-primary': 'done'
	},

	/**
	 * Returns an import results message for display in a dialog.
	 * Shown for non-scheduled imports where results are immediately
	 * displayed to the user rather than being saved to the db.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Object} options.results - results from running the import
	 * @param {String} [options.results.countMessageKey] - if specified,
	 * use this key for the count messages
	 * @param {String} options.countMessageKey - key for the count phrase.
	 * (This should be a message key that is compatible with i18n.countMessage)
	 * @param {String} options.problemMessageKey
	 */
	initialize: function (options = {}) {
		this.options = options
	},

	render: function () {
		this.$el.empty().append(template(this.context()))
		if (this.options.results.status === 'success') {
			this.$('.skipped-count-message').addClass('is-not-displayed')
		}

		let addedCount = this.options.results.addedCount
		let updatedCount = this.options.results.updatedCount
		let successCount = this.options.results.successCount
		let pendingCount = this.options.results.pendingCount

		// #51873 addedCount and updatedCount may now be initialized to 0
		// (since #51859), so determine if this is a user import by the
		// count message key instead
		if (this.isUserImport()) {
			// for user imports, we want to display updated and added counts
			// in separate lines
			if (addedCount > 0) {
				this.$('.added-count-message').removeClass('is-not-displayed')
			}
			if (updatedCount > 0) {
				this.$('.updated-count-message').removeClass('is-not-displayed')
			}
			// hide the regular success count message
			// unless 0 users were added or updated
			if (successCount && successCount != 0) {
				this.$('.success-count-message').addClass('is-not-displayed')
			}
		}

		// #29369 for import results that were migrated from Connect
		// we won't have success counts
		if (this.options.results.migratedResults) {
			if (this.options.results.status === 'partial-success') {
				// hide the success count message, only show failed
				this.$('.success-count-message').addClass('is-not-displayed')
			}
		}
		return this
	},

	context: function () {
		let title = i18n.get('admin.import.finished')

		let successCount = this.options.results.successCount || 0
		let skippedCount = this.options.results.skippedCount || 0
		let addedCount = this.options.results.addedCount
		let updatedCount = this.options.results.updatedCount
		let pendingCount = this.options.results.pendingCount || 0
		// #32473 the count message key may be returned as part of the results
		let countKey = this.options.results.countMessageKey || this.options.countMessageKey

		let successCountMessage = this.countMessage(
			'admin.import.successCountMessage', countKey, successCount)
		let skippedCountMessage = this.countMessage(
			'admin.import.problemCountMessage', countKey, skippedCount)
		let addedCountMessage = this.countMessage(
			'admin.import.addedCountMessage', countKey, addedCount)
		let updatedCountMessage = this.countMessage(
			'admin.import.updatedCountMessage', countKey, updatedCount)
		// #24350 Only display this message if there are pending rows
		let pendingCountMessage = null
		if (pendingCount) {
			// Note: Can't use the `this.countMessage` that everything else is using
			// because this message's pluralization is more involved
			const highlighted = highlight(i18n.countMessage(countKey, pendingCount))
			pendingCountMessage = i18n.countMessage('admin.import.pendingCountMessage', pendingCount, highlighted)
		}
		// #13930 allow a more generic message, needed for
		// scorecard item imports, when the whole import fails on any error
		if (this.options.problemMessageKey) {
			skippedCountMessage = i18n.get(this.options.problemMessageKey)
		}

		let status = this.options.results.status
		let icon = status === 'success' ? 'blue-check' : 'warn'

		// #29369 for import results that were migrated from Connect, we won't
		// have success counts so we need to make a more generic message
		if (this.options.results.migratedResults) {
			if (this.options.results.status === 'success') {
				// "All rows imported"
				successCountMessage = this.countMessage(
					'admin.import.successCountMessage', countKey,
					i18n.get('common.all'))
			}
		}

		return {
			title,
			successCountMessage,
			skippedCountMessage,
			addedCountMessage,
			updatedCountMessage,
			pendingCountMessage,
			icon
		}
	},

	moreInfo: function () {
		let problemMessage = 'admin.import.notImported'
		if (this.options.problemMessageKey) {
			problemMessage = this.options.problemMessageKey
		}
		appDisplay.dialog(importProblemsTemplate({
			problemMessage,
			summaries: this.options.results.importProblemSummaries,
			importProblems: this.options.results.importProblems
		}))
	},

	/**
	 * Determines if the import results dialog is displaying results for a user
	 * import
	 * @returns {boolean}
	 */
	isUserImport: function() {
		let countKey = this.options.results?.countMessageKey || this.options.countMessageKey
		return countKey === 'admin.import.user'
	},

	countMessage: function (messageKey, countKey, count) {
		return i18n.get(messageKey, highlight(i18n.countMessage(countKey, count)))
	},

	done: function() {
		this.trigger('done')
	}

})

export default ImportResultsDialog
ImportResultsDialog.__filename__ = 'components/import-results-dialog.js'
